import React, { useMemo } from 'react';
import { CalendarCheck2, CalendarClock, CheckCircle2, XCircle, TrendingUp, Calendar, LayoutDashboard } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useBookings, useUpcomingBookings } from '../../hooks/useBooking';
import { useBookingSettings } from '../../hooks/useBookingSettings';
import { BookingStatus } from '../../types/booking';
import BookingStatsCard from '../../components/booking/BookingStatsCard';
import BookingDashboardCard from '../../components/booking/BookingDashboardCard';
import CalendarSyncStatus from '../../components/booking/CalendarSyncStatus';
import CrmSyncStatus from '../../components/booking/CrmSyncStatus';
import BookingStatusBadge from '../../components/booking/BookingStatusBadge';
import Spinner from '../../components/Spinner';

export default function BookingDashboard() {
  const { data: bookings, isLoading } = useBookings();
  const { data: upcoming } = useUpcomingBookings();
  const { data: settings, isLoading: loadingSettings } = useBookingSettings();

  const stats = useMemo(() => {
    const all = bookings ?? [];
    const completed = all.filter((b) => b.status === BookingStatus.Completed).length;
    const cancelled = all.filter((b) => b.status === BookingStatus.Cancelled).length;
    const bookingRate = all.length ? Math.round(((all.length - cancelled) / all.length) * 100) : 0;

    const byDate = new Map<string, number>();
    const byService = new Map<string, number>();
    all.forEach((b) => {
      const date = new Date(b.bookingDate).toLocaleDateString('default', { month: 'short', day: 'numeric' });
      byDate.set(date, (byDate.get(date) ?? 0) + 1);
      byService.set(b.serviceName, (byService.get(b.serviceName) ?? 0) + 1);
    });

    return {
      totalBookings: all.length,
      upcomingBookings: upcoming?.length ?? 0,
      completedBookings: completed,
      cancelledBookings: cancelled,
      bookingRate,
      bookingsOverTime: Array.from(byDate, ([date, count]) => ({ date, count })),
      bookingsByService: Array.from(byService, ([serviceName, count]) => ({ serviceName, count })),
    };
  }, [bookings, upcoming]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Spinner size="h-8 w-8" />
        <p className="mt-3 text-strivre-mutedtext">Loading booking dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div>
        <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2"><LayoutDashboard size={22} /> Booking Dashboard</h1>
        <p className="text-sm text-strivre-mutedtext">Overview of scheduling activity, sync health, and upcoming appointments.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <BookingStatsCard title="Total Bookings" value={stats.totalBookings} icon={<CalendarCheck2 size={20} />} />
        <BookingStatsCard title="Upcoming Bookings" value={stats.upcomingBookings} icon={<CalendarClock size={20} />} />
        <BookingStatsCard title="Completed Bookings" value={stats.completedBookings} icon={<CheckCircle2 size={20} />} />
        <BookingStatsCard title="Cancelled Bookings" value={stats.cancelledBookings} icon={<XCircle size={20} />} highlight />
        <BookingStatsCard title="Booking Rate" value={`${stats.bookingRate}%`} icon={<TrendingUp size={20} />} />
        <BookingStatsCard
          title="Calendar Sync Status"
          value={settings?.calendarSyncEnabled ? 'Enabled' : 'Disabled'}
          icon={<Calendar size={20} />}
        />
        {/* <BookingStatsCard
          title="CRM Sync Status"
          value={settings?.crmSyncEnabled ? 'Enabled' : 'Disabled'}
          icon={<Database size={20} />}
        /> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BookingDashboardCard title="Bookings Over Time">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats.bookingsOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF4" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#002144" fill="#E8EEF4" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </BookingDashboardCard>

        <BookingDashboardCard title="Bookings By Service">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.bookingsByService}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF4" />
              <XAxis dataKey="serviceName" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#801414" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </BookingDashboardCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BookingDashboardCard title="Upcoming Schedule" className="lg:col-span-1">
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {(upcoming ?? []).length === 0 && (
              <p className="text-sm text-strivre-mutedtext text-center py-6">No upcoming bookings.</p>
            )}
            {upcoming?.map((b) => (
              <div key={b.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-3">
                <div>
                  <p className="text-sm font-semibold text-strivre-navy">{b.customerFirstName} {b.customerLastName}</p>
                  <p className="text-xs text-strivre-mutedtext">{b.serviceName} · {new Date(b.bookingDate).toLocaleDateString()} {new Date(b.bookingDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <BookingStatusBadge status={b.status} />
              </div>
            ))}
          </div>
        </BookingDashboardCard>

        <BookingDashboardCard title="Calendar Sync">
          <CalendarSyncStatus settings={settings} loading={loadingSettings} />
        </BookingDashboardCard>

        <BookingDashboardCard title="CRM Sync">
          <CrmSyncStatus settings={settings} loading={loadingSettings} />
        </BookingDashboardCard>
      </div>
    </div>
  );
}

